import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { AIRPORT_REQUIRED } from '@/types/airport';
import type { Airport, AirportRequiredField } from '@/types/airport';
import { createAirport, deleteAirport, editAirport } from './api';
import type { AirportWriteResult } from './api';
import { TimeZonePicker } from './TimeZonePicker';
import styles from './AirportPanel.module.css';

/**
 * One airport, in a dialog over the table — read, created or edited.
 *
 * **An edit is a replace.** `POST /airport/edit/id/{id}` expects the whole object
 * back with the changes applied, so anything missing from the body is a column being
 * cleared. That is the single most important thing about this form: the body is
 * built by spreading the row that was LOADED and overwriting only the inputs, so a
 * column this app does not model — `icaoCode` is one, and the dictionary may hold
 * others — survives a save instead of being wiped by it. Building the body from the
 * form's own fields would look identical on screen and quietly destroy data.
 *
 * **Deletion is not soft.** `active: false` hides an airport from selection while
 * keeping the row, and that is what this panel offers first; deleting is behind a
 * confirmation and says what it does.
 *
 * Reading is open to four roles and writing to `ADMIN` alone, so the same panel
 * serves as a read-only card: the inputs become `readOnly` — not `disabled`, which
 * would make them unreadable and unselectable — and the actions disappear.
 */

interface AirportPanelProps {
  /** The airport being edited, or null for the create form. */
  airport: Airport | null;
  /** Whether this role may write. `false` renders the same fields, read-only. */
  canWrite: boolean;
  onSaved: (airport: Airport, action: 'created' | 'saved') => void;
  onDeleted: (airport: Airport) => void;
  onClose: () => void;
}

/**
 * The form's fields, all as strings — an input has no other kind of value.
 *
 * **`icaoCode` is deliberately not here.** It can be filtered and sorted on, but the
 * documented object does not carry it and the documented write body has no field for
 * it — so an input would either be ignored by the server or, worse, send `""` for a
 * column the list response never showed us and clear it on the first save. It is
 * displayed where the row has one and otherwise left entirely alone, which is what
 * the spread in `handleSubmit` guarantees.
 */
interface Draft {
  airportIataCode: string;
  airportNameEn: string;
  airportNameRu: string;
  airportNameKz: string;
  cityNameEn: string;
  cityNameRu: string;
  cityNameKz: string;
  countryCode: string;
  countryNameEn: string;
  countryNameRu: string;
  countryNameKz: string;
  localTimeZone: string;
  latitude: string;
  longitude: string;
  active: boolean;
  icaoCode:string;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const draftFrom = (airport: Airport | null): Draft => ({
  airportIataCode: text(airport?.airportIataCode),
  airportNameEn: text(airport?.airportNameEn),
  airportNameRu: text(airport?.airportNameRu),
  airportNameKz: text(airport?.airportNameKz),
  cityNameEn: text(airport?.cityNameEn),
  cityNameRu: text(airport?.cityNameRu),
  cityNameKz: text(airport?.cityNameKz),
  countryCode: text(airport?.countryCode),
  countryNameEn: text(airport?.countryNameEn),
  countryNameRu: text(airport?.countryNameRu),
  countryNameKz: text(airport?.countryNameKz),
  localTimeZone: text(airport?.localTimeZone),
  latitude: text(airport?.latitude),
  longitude: text(airport?.longitude),
  // A new airport is meant to be usable, and the server defaults an omitted `active`
  // to false — so the form states it either way rather than relying on a default
  // that is the opposite of the intent.
  active: airport?.active ?? true,
  icaoCode: text(airport?.icaoCode),
});

/**
 * The form's fields as the API names them.
 *
 * The codes are trimmed AND upper-cased here as well as on the server, so what the
 * form shows after a save is what the dictionary holds. Everything else is trimmed
 * only: an airport name is not a code and `"O'Hare"` is not `"O'HARE"`.
 */
const bodyFrom = (draft: Draft): Partial<Airport> => ({
  airportIataCode: draft.airportIataCode.trim().toUpperCase(),
  airportNameEn: draft.airportNameEn.trim(),
  airportNameRu: draft.airportNameRu.trim(),
  airportNameKz: draft.airportNameKz.trim(),
  cityNameEn: draft.cityNameEn.trim(),
  cityNameRu: draft.cityNameRu.trim(),
  cityNameKz: draft.cityNameKz.trim(),
  countryCode: draft.countryCode.trim().toUpperCase(),
  countryNameEn: draft.countryNameEn.trim(),
  countryNameRu: draft.countryNameRu.trim(),
  countryNameKz: draft.countryNameKz.trim(),
  localTimeZone: draft.localTimeZone.trim(),
  latitude: draft.latitude.trim(),
  longitude: draft.longitude.trim(),
  active: draft.active,
});

export const AirportPanel = ({
  airport,
  canWrite,
  onSaved,
  onDeleted,
  onClose,
}: AirportPanelProps) => {
  const { t, formatDateTime } = useTranslation();

  const isCreate = airport === null;

  // No sync effect: the panel is keyed by the selection in AirportsPage, so choosing
  // another row remounts it and this initialiser runs again. An effect copying the
  // prop into state would set state during the commit and overwrite what is being
  // typed.
  const [draft, setDraft] = useState<Draft>(() => draftFrom(airport));
  const [missingFields, setMissingFields] = useState<readonly AirportRequiredField[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /**
   * Whether closing now would throw away something typed. While it would, a click
   * beside the dialog does nothing; Escape, Cancel and × still close it.
   */
  const isDirty = JSON.stringify(draft) !== JSON.stringify(draftFrom(airport));

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    // Both messages the form can carry are verdicts on a submission that no longer
    // describes what is on screen.
    setMissingFields((current) => current.filter((field) => field !== key));
    setFormError(null);
  };

  /** Runs a write and reports it, so create, edit and delete share one path. */
  const run = async (write: () => Promise<AirportWriteResult>, action: 'created' | 'saved') => {
    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await write();

      if (result.ok) {
        onSaved(result.airport, action);
        return;
      }

      // The server's own sentence — a duplicate code, a blank field, an id that is
      // gone. It arrives in Russian and is shown as written: rewording a message
      // that names a specific row would lose the part that identifies it.
      setFormError(result.message ?? t('airports.panel.saveFailed'));
    } catch (caught) {
      setFormError(messageFor(caught, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const body = bodyFrom(draft);

    // The five fields the server rejects a write without. Checked here because the
    // rule is documented and exact — everything else is the server's to judge, and
    // guessing at its rules would refuse writes the API would have accepted.
    const missing = AIRPORT_REQUIRED.filter((field) => text(body[field]).trim() === '');

    if (missing.length > 0) {
      setMissingFields(missing);
      setFormError(t('airports.panel.missingRequired'));
      return;
    }

    setMissingFields([]);

    if (airport === null) {
      void run(() => createAirport(body), 'created');
      return;
    }

    // THE WHOLE ROW, with the form applied over it. See the note at the top of this
    // file: the endpoint replaces rather than patches, so a column that is not in
    // this object is a column being cleared.
    void run(() => editAirport(airport.id, { ...airport, ...body }), 'saved');
  };

  const handleDelete = async () => {
    if (!airport) return;

    setFormError(null);
    setIsSubmitting(true);

    try {
      const result = await deleteAirport(airport.id);

      if (result.ok) {
        onDeleted(airport);
        return;
      }

      setFormError(result.message ?? t('airports.panel.deleteFailed'));
      setConfirmingDelete(false);
    } catch (caught) {
      setFormError(messageFor(caught, t));
      setConfirmingDelete(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const invalid = (field: keyof Draft) =>
    missingFields.includes(field as AirportRequiredField)
      ? { error: t('common.required') }
      : {};

  /** Every text input on this form takes the same three props. */
  const common = {
    disabled: isSubmitting,
    readOnly: !canWrite,
    autoComplete: 'off' as const,
  };

  const content = (
    <>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.code}>
            {isCreate
              ? t('airports.panel.create')
              : (airport.airportIataCode ?? t('airports.panel.noCode'))}
          </span>

          {!isCreate && (
            <div className={styles.badges}>
              <Badge tone={airport.active ? 'success' : 'neutral'}>
                {t(airport.active ? 'airports.states.active' : 'airports.states.inactive')}
              </Badge>
              {typeof airport.icaoCode === 'string' && airport.icaoCode !== '' && (
                <Badge code>{airport.icaoCode}</Badge>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          ×
        </button>
      </header>

      <div className={styles.body}>
        {/* Said once, at the top, rather than as a disabled control the reader has to
            work out the reason for. */}
        {!canWrite && <p className={styles.readOnly}>{t('airports.panel.readOnly')}</p>}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {formError && (
            // Announced when it appears: a reader whose focus is still in the field
            // that caused it would otherwise never learn why nothing happened.
            <p className={styles.error} role="alert">
              {formError}
            </p>
          )}

          <Section >
            <Field
              label={t('airports.columns.iata')}
              value={draft.airportIataCode}
              onChange={(event) => set('airportIataCode', event.target.value.toUpperCase())}
              {...invalid('airportIataCode')}
              maxLength={3}
              required
              {...common}
            />

            <Field
              label={t('airports.columns.icao')}
              value={draft.icaoCode}
              onChange={(event) => set('icaoCode', event.target.value.toUpperCase())}
              {...invalid('icaoCode')}
              maxLength={4}
              {...common}
            />

          </Section>

          <Section title={t('airports.panel.airportName')}>
            <Languages>
              <Field
                label={t('airports.panel.inEnglish')}
                value={draft.airportNameEn}
                onChange={(event) => set('airportNameEn', event.target.value)}
                {...invalid('airportNameEn')}
                required
                {...common}
              />
              <Field
                label={t('airports.panel.inRussian')}
                value={draft.airportNameRu}
                onChange={(event) => set('airportNameRu', event.target.value)}
                {...common}
              />
              <Field
                label={t('airports.panel.inKazakh')}
                value={draft.airportNameKz}
                onChange={(event) => set('airportNameKz', event.target.value)}
                {...common}
              />
            </Languages>
          </Section>

          <Section title={t('airports.columns.city')}>
            <Languages>
              <Field
                label={t('airports.panel.inEnglish')}
                value={draft.cityNameEn}
                onChange={(event) => set('cityNameEn', event.target.value)}
                {...invalid('cityNameEn')}
                required
                {...common}
              />
              <Field
                label={t('airports.panel.inRussian')}
                value={draft.cityNameRu}
                onChange={(event) => set('cityNameRu', event.target.value)}
                {...common}
              />
              <Field
                label={t('airports.panel.inKazakh')}
                value={draft.cityNameKz}
                onChange={(event) => set('cityNameKz', event.target.value)}
                {...common}
              />
            </Languages>
          </Section>

          <Section title={t('airports.columns.country')}>
            <Field
              label={t('airports.panel.countryCode')}
              value={draft.countryCode}
              onChange={(event) => set('countryCode', event.target.value.toUpperCase())}
              {...invalid('countryCode')}
              required
              {...common}
            />
            <Languages>
              <Field
                label={t('airports.panel.inEnglish')}
                value={draft.countryNameEn}
                onChange={(event) => set('countryNameEn', event.target.value)}
                {...invalid('countryNameEn')}
                required
                {...common}
              />
              <Field
                label={t('airports.panel.inRussian')}
                value={draft.countryNameRu}
                onChange={(event) => set('countryNameRu', event.target.value)}
                {...common}
              />
              <Field
                label={t('airports.panel.inKazakh')}
                value={draft.countryNameKz}
                onChange={(event) => set('countryNameKz', event.target.value)}
                {...common}
              />
            </Languages>
          </Section>

          <Section title={t('airports.panel.place')}>
            {/* Latitude and longitude travel as STRINGS, which is the API's own
                choice and is kept rather than parsed: a decimal parsed into a number
                and printed back can lose a digit of precision, and nothing here does
                arithmetic on them. */}
            <Field
              label={t('airports.panel.latitude')}
              value={draft.latitude}
              onChange={(event) => set('latitude', event.target.value)}
              inputMode="decimal"
              spellCheck={false}
              {...common}
            />
            <Field
              label={t('airports.panel.longitude')}
              value={draft.longitude}
              onChange={(event) => set('longitude', event.target.value)}
              inputMode="decimal"
              spellCheck={false}
              {...common}
            />

            <TimeZonePicker
              label={t('airports.columns.timeZone')}
              value={draft.localTimeZone}
              onChange={(zone) => set('localTimeZone', zone)}
              disabled={isSubmitting}
              readOnly={!canWrite}
            />
            
            <div className={styles.field}>
              <span className={styles.label}>{t('airports.columns.status')}</span>
              <Select
                size="md"
                options={[
                  { value: 'true', label: t('airports.states.active') },
                  { value: 'false', label: t('airports.states.inactive') },
                ]}
                value={draft.active ? 'true' : 'false'}
                onChange={(event) => set('active', event.target.value === 'true')}
                disabled={isSubmitting || !canWrite}
                aria-label={t('airports.columns.status')}
              />
            </div>
          </Section>

          {!isCreate && (airport.created || airport.updated) && (
            <dl className={styles.stamps}>
              {airport.created && (
                <div className={styles.stamp}>
                  <dt>{t('airports.columns.created')}</dt>
                  <dd>{formatDateTime(airport.created)}</dd>
                </div>
              )}
              {airport.updated && (
                <div className={styles.stamp}>
                  <dt>{t('airports.columns.updated')}</dt>
                  <dd>{formatDateTime(airport.updated)}</dd>
                </div>
              )}
            </dl>
          )}

          {canWrite && (
            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? t('airports.panel.saving')
                  : t(isCreate ? 'airports.panel.createAction' : 'common.save')}
              </Button>
              <Button onClick={onClose} disabled={isSubmitting}>
                {t('common.cancel')}
              </Button>
            </div>
          )}
        </form>

        {/*
          Outside the form, and last.

          The row is REMOVED — this is the one destructive action in the app, and it
          is on shared master data that flights are matched against. Two steps, and
          the safer alternative named where the reader is standing: an airport that
          should stop being offered wants `active: false`, not deletion.
        */}
        {canWrite && !isCreate && (
          <section className={styles.danger}>
            <div className={styles.dangerText}>
              <h3 className={styles.dangerTitle}>{t('airports.panel.deleteTitle')}</h3>
              <p className={styles.dangerHint}>
                {confirmingDelete
                  ? t('airports.panel.deleteConfirm', {
                      code: airport.airportIataCode ?? String(airport.id),
                    })
                  : t('airports.panel.deleteHint')}
              </p>
            </div>

            {confirmingDelete ? (
              <div className={styles.dangerActions}>
                <Button variant="danger" onClick={() => void handleDelete()} disabled={isSubmitting}>
                  {t('airports.panel.deleteConfirmAction')}
                </Button>
                <Button onClick={() => setConfirmingDelete(false)} disabled={isSubmitting}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <Button variant="danger" onClick={() => setConfirmingDelete(true)} disabled={isSubmitting}>
                {t('common.delete')}
              </Button>
            )}
          </section>
        )}
      </div>
    </>
  );

  /**
   * A dialog over the table, for a new airport and an existing one alike.
   *
   * A column beside the table squeezed every row whenever an airport was open, and
   * reflowed the table again on close. Over it, the table keeps its width the whole
   * time, and the form gets the room a form wants.
   *
   * Wide: this form is fifteen fields, and three name triples read as rows of three
   * rather than as a column of nine.
   */
  return (
    <Modal
      label={t(isCreate ? 'airports.panel.create' : 'airports.panel.edit')}
      onClose={onClose}
      width="lg"
      dismissible={!isDirty && !isSubmitting}
    >
      {content}
    </Modal>
  );
};

/**
 * A failed request, told apart from a rejected one.
 *
 * A 403 here means this role may not write to the dictionary — it is not a
 * validation error, and offering "try again" would be wrong twice over.
 */
const messageFor = (caught: unknown, t: (key: Key) => string): string => {
  const status = caught instanceof ApiError ? caught.status : 0;

  if (status === 403) return t('airports.panel.forbidden');
  return status === 0 ? t('airports.panel.networkError') : t('airports.panel.serverError');
};

/** A group of fields, under a heading when it needs one — the codes do not. */
const Section = ({ title, children }: { title?: string; children: ReactNode }) => (
  <section className={styles.section}>
    {title && <h3 className={styles.sectionTitle}>{title}</h3>}
    <div className={styles.grid}>{children}</div>
  </section>
);

/**
 * One name in its three languages. They are one value written three ways, so they
 * share a row — left to the section's grid, a narrow card wraps them two and one,
 * and the Kazakh reads as a different field from the pair above it.
 */
const Languages = ({ children }: { children: ReactNode }) => (
  <div className={styles.languages}>{children}</div>
);
